import {
  Dropdown,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuItem,
  Stack,
  Tag,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import PypestreamLogo from '@pypestream/design-system/assets/pypestream-logo-black.svg?react';
import { useTranslation } from '@pypestream/translations';
import { CSSProperties, FC } from 'react';

import { capitalizeWords, getEnvDotStyle } from '../../utils';
import { LONG_NAME_REACT_STYLE } from '../../utils/long-name-style';
import { sendProStudioEvent } from '../../xstate/app.xstate';

const KEBAB_TRIGGER_ID = 'ps-channel-settings-header-actions';

const TITLE_GROUP_STYLE: CSSProperties = { flex: '1 1 0', minWidth: 0 };
const TITLE_STYLE: CSSProperties = { minWidth: 0 };

/**
 * The Channel Settings page header (PSTUDIO-3768): the channel's own name as the
 * page title with its environment beside it, the Pype product mark on the right,
 * and a kebab for the actions that used to be fields.
 *
 * The kebab holds what the page does not show (PSTUDIO-2064): the App (Channel)
 * ID and the backend pype's id and name — the Pype name stopped being
 * user-facing, so this is the only place it is reachable. Copying is read-only,
 * so viewers get the menu too.
 *
 * Renaming: the settings tab renames inline in its Details card (PSTUDIO-2064,
 * per the design), so it passes no `onRename`. A route without that card (the
 * Pype Next tab) still passes one and gets the Edit item.
 */
export const ChannelSettingsHeader: FC<{
  name: string;
  /** Raw channel env, e.g. `live` / `sandbox`. */
  env: string;
  /** The App (Channel) ID: the widget id. */
  channelId: string;
  /** The backend pype behind the channel. */
  pypeId: string;
  pypeName: string;
  onRename?: () => void;
  /** Viewers can copy but not rename. */
  isViewer: boolean;
}> = ({ name, env, channelId, pypeId, pypeName, onRename, isViewer }) => {
  const [t] = useTranslation();
  const copy = (value: string, key: string, defaultValue: string) => {
    if (!value) return;
    sendProStudioEvent({
      type: 'copyToClipboard',
      value,
      message: t(`pro-studio/channels:toasts.${key}`, { defaultValue }),
    });
  };

  return (
    <Stack
      test-id="ps-channel-settings-header"
      justifyContent="space-between"
      alignItems="center"
      gutter="small"
    >
      {/* Grows into the row and may shrink below its content: with the name
          bounded (`LONG_NAME_REACT_STYLE`), a 255-character name wraps and
          clamps here instead of pushing the header past the page edge. */}
      <Stack
        test-id="ps-channel-settings-title-group"
        alignItems="center"
        gutter="small"
        nowrap
        style={TITLE_GROUP_STYLE}
      >
        <TextTitle
          test-id="ps-channel-settings-title"
          size="small"
          style={TITLE_STYLE}
        >
          <span data-long-name title={name} style={LONG_NAME_REACT_STYLE}>
            {name}
          </span>
        </TextTitle>
        {/* `success` is the light-green env pill already used for live elsewhere
          (project configurations, the channels table); sandbox stays neutral. */}
        <Tag
          test-id="ps-channel-settings-env-tag"
          size="small"
          minimal
          variant={env.toLowerCase() === 'live' ? 'success' : 'default'}
        >
          <Stack alignItems="center" gutter="2xsmall" nowrap>
            <span style={getEnvDotStyle(env, true)} />
            {capitalizeWords(env)}
          </Stack>
        </Tag>
      </Stack>

      <Stack alignItems="center" gutter="small" nowrap>
        <Stack
          test-id="ps-channel-settings-header-pype-logo"
          alignItems="center"
          gutter="xsmall"
          nowrap
        >
          {/* The Pypestream mark, as on the create card's Details title — the
            designer swapped the Pype mark for it (PSTUDIO-2064). */}
          <Image
            width="24"
            height="24"
            src={PypestreamLogo as unknown as string}
          />
          <TextBody>Pype</TextBody>
        </Stack>
        <IconButton
          test-id="ps-channel-settings-header-kebab"
          name="kebab"
          size="small"
          label={
            t('pro-studio/channels:menu.channelActions', {
              defaultValue: 'Channel actions',
            }) ?? 'Channel actions'
          }
          has-popup="menu"
          id={KEBAB_TRIGGER_ID}
        />
        <Dropdown trigger={KEBAB_TRIGGER_ID} placement="bottom-end">
          <Menu>
            {onRename && !isViewer && (
              <MenuItem
                test-id="ps-channel-settings-header-rename"
                onClick={onRename}
              >
                <Icon name="edit" size="small" slot="prefix" />
                Edit
              </MenuItem>
            )}
            <MenuItem
              test-id="ps-channel-settings-header-copy-app-id"
              onClick={() =>
                copy(
                  channelId,
                  'appIdCopied',
                  'App ID has been copied to your clipboard.'
                )
              }
            >
              <Icon name="copy" size="small" slot="prefix" />
              {t('pro-studio/channels:menu.copyAppId', {
                defaultValue: 'Copy App (Channel) ID',
              })}
            </MenuItem>
            <MenuItem
              test-id="ps-channel-settings-header-copy-pype-id"
              onClick={() =>
                copy(
                  pypeId,
                  'pypeIdCopied',
                  'Pype ID has been copied to your clipboard.'
                )
              }
            >
              <Icon name="copy" size="small" slot="prefix" />
              {t('pro-studio/channels:menu.copyPypeId', {
                defaultValue: 'Copy Pype ID (Backend)',
              })}
            </MenuItem>
            <MenuItem
              test-id="ps-channel-settings-header-copy-pype-name"
              onClick={() =>
                copy(
                  pypeName,
                  'pypeNameCopied',
                  'Pype name has been copied to your clipboard.'
                )
              }
            >
              <Icon name="copy" size="small" slot="prefix" />
              {t('pro-studio/channels:menu.copyPypeName', {
                defaultValue: 'Copy Pype Name (Backend)',
              })}
            </MenuItem>
          </Menu>
        </Dropdown>
      </Stack>
    </Stack>
  );
};
